import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Search, ShoppingBag, Sparkles, ShieldCheck, Zap, Gift,
  ChevronLeft, ChevronRight, User, Heart, ChevronDown,
  Utensils, Shirt, Plane, Tv, ShoppingCart, Smartphone, Coffee, Briefcase,
  Clock,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const giftApi = axios.create({ baseURL: `${BACKEND}/api/gifts`, timeout: 20000 });

const CATEGORY_META = {
  "food": { label: "Food & Dining", icon: Utensils, color: "#F97316" },
  "fashion": { label: "Fashion", icon: Shirt, color: "#EC4899" },
  "travel": { label: "Travel", icon: Plane, color: "#0EA5E9" },
  "entertainment": { label: "Entertainment", icon: Tv, color: "#A855F7" },
  "grocery": { label: "Grocery", icon: ShoppingCart, color: "#22C55E" },
  "electronics": { label: "Electronics", icon: Smartphone, color: "#3B82F6" },
  "lifestyle": { label: "Lifestyle", icon: Coffee, color: "#F59E0B" },
  "ai-software": { label: "Software", icon: Briefcase, color: "#64748B" },
};

const HERO_SLIDES = [
  {
    eyebrow: "Festive picks · this week",
    h1: ["Gifting that feels", "precious."],
    sub: "100+ brands. Voucher in their inbox in seconds. Wrapped in a touch of gold.",
    accent: "#C9A227",
    bg: "linear-gradient(135deg, #FFFBF3 0%, #FFF4D6 50%, #FCE9B5 100%)",
    cta: { label: "Shop gift cards", href: "#brands" },
  },
  {
    eyebrow: "New · Amazon, Myntra, Zomato",
    h1: ["Big brands.", "Bigger smiles."],
    sub: "Up to 8% cashback. Instant delivery. UPI, cards, and net-banking accepted.",
    accent: "#1A1A1A",
    bg: "linear-gradient(135deg, #0F0F0F 0%, #1F1F1F 60%, #2A2A2A 100%)",
    dark: true,
    cta: { label: "View deals", href: "#deals" },
  },
  {
    eyebrow: "Corporate gifting",
    h1: ["Bulk orders,", "single invoice."],
    sub: "Send to 1,000+ recipients with a personalised note. Settlement reconciled to the rupee.",
    accent: "#8C6B0E",
    bg: "linear-gradient(135deg, #FFFFFF 0%, #F8EFD9 100%)",
    cta: { label: "Talk to sales", href: "mailto:gifts@payflex.in" },
  },
];

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

// ── Recently-viewed persistence (localStorage) ──────────────────────────
const RV_KEY = "qrzone:recentlyViewed:v1";
const RV_MAX = 10;

function readRecentlyViewed() {
  try {
    const raw = localStorage.getItem(RV_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function pushRecentlyViewed(brand) {
  if (!brand?.slug) return;
  try {
    const list = readRecentlyViewed().filter((b) => b.slug !== brand.slug);
    list.unshift({
      id: brand.id, slug: brand.slug, name: brand.name,
      logo_url: brand.logo_url, brand_color: brand.brand_color,
      category: brand.category, is_featured: brand.is_featured,
      instant_delivery: brand.instant_delivery,
      denominations: brand.denominations,
      viewed_at: Date.now(),
    });
    localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, RV_MAX)));
  } catch { /* quota / private mode */ }
}

/**
 * /gifts — Premium Gyftr-style storefront on top of PayFlex's customer DB.
 * Layout: top utility bar → main nav with search → rotating hero → category icons
 * → offer banners → trending brand grid → why-us strip → footer.
 */
export default function GiftStorefront() {
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [recentlyViewed, setRecentlyViewed] = useState([]);

  useEffect(() => { setRecentlyViewed(readRecentlyViewed()); }, []);

  // Keep RV in sync across tabs / nav-back
  useEffect(() => {
    const onStorage = (e) => { if (e.key === RV_KEY) setRecentlyViewed(readRecentlyViewed()); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const onTileClick = (brand) => {
    pushRecentlyViewed(brand);
    setRecentlyViewed(readRecentlyViewed());
  };

  const clearRecentlyViewed = () => {
    try { localStorage.removeItem(RV_KEY); } catch { /* noop */ }
    setRecentlyViewed([]);
  };

  useEffect(() => {
    (async () => {
      try {
        const [b, c] = await Promise.all([giftApi.get("/brands"), giftApi.get("/categories")]);
        setBrands(b.data?.items || []);
        setCategories(c.data?.items || []);
      } catch (_) { /* empty state */ }
      finally { setLoading(false); }
    })();
  }, []);

  const visible = useMemo(() => {
    const lower = q.trim().toLowerCase();
    return brands.filter((b) => {
      if (activeCat !== "all" && b.category !== activeCat) return false;
      if (lower && !((b.name || "").toLowerCase().includes(lower))) return false;
      return true;
    });
  }, [brands, activeCat, q]);

  const featured = brands.filter((b) => b.is_featured);
  const trending = brands.slice(0, 10);
  const recommended = brands.length > 1 ? [...brands].reverse().slice(0, 10) : brands;

  return (
    <div className="min-h-screen bg-white text-[#1A1A1A]" data-testid="gift-storefront">
      <TopBar />
      <MainNav q={q} setQ={setQ} />
      <HeroCarousel />
      <CategoryRow categories={categories} activeCat={activeCat} setActiveCat={setActiveCat} totalBrands={brands.length} />
      <RecentlyViewedRail items={recentlyViewed} onClick={onTileClick} onClear={clearRecentlyViewed} />
      <OfferBanners />
      <BrandSection title="Trending now" subtitle="Hot picks this week" brands={trending} loading={loading} testId="trending" onTileClick={onTileClick} />
      {featured.length > 0 && (
        <BrandSection title="Editor's choice" subtitle="Curated by the QR Zone team" brands={featured} loading={false} testId="featured" onTileClick={onTileClick} />
      )}
      <BrandSection
        id="brands"
        title="All brands"
        subtitle={`${visible.length} of ${brands.length}`}
        brands={visible}
        loading={loading}
        empty={brands.length > 0 && visible.length === 0}
        onClear={() => { setQ(""); setActiveCat("all"); }}
        testId="all"
        onTileClick={onTileClick}
      />
      <BrandSection title="You might like" subtitle="Based on popular picks" brands={recommended} loading={loading} testId="recommended" onTileClick={onTileClick} />
      <WhyUs />
      <Footer />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
function TopBar() {
  return (
    <div className="bg-[#1A1A1A] text-white text-xs">
      <div className="max-w-7xl mx-auto px-4 lg:px-12 py-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[11px] text-neutral-300">
          <Sparkles className="size-3 text-[#E8B923]" />
          <span>Free delivery on every voucher · settlement in 1 working day</span>
        </div>
        <div className="hidden sm:flex items-center gap-4 text-[11px] text-neutral-300">
          <a href="#" className="hover:text-white transition-colors">Bulk orders</a>
          <a href="#" className="hover:text-white transition-colors">Help</a>
          <Link to="/login" className="hover:text-white transition-colors">Partner sign-in</Link>
        </div>
      </div>
    </div>
  );
}

function MainNav({ q, setQ }) {
  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-neutral-200 shadow-[0_2px_8px_-4px_rgba(0,0,0,0.05)]">
      <div className="max-w-7xl mx-auto px-4 lg:px-12 py-3.5 flex items-center gap-5 lg:gap-10">
        <Link to="/gifts" className="flex items-center gap-2.5 shrink-0" data-testid="gift-brand-logo">
          <div className="size-10 rounded-xl bg-gradient-to-br from-[#C9A227] to-[#6F540B] flex items-center justify-center shadow-md">
            <Gift className="size-5 text-white" />
          </div>
          <div className="leading-tight">
            <div className="font-serif text-2xl font-semibold tracking-tight">QR <span className="text-[#8C6B0E]">Zone</span></div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-neutral-500 -mt-0.5">Premium gifting</div>
          </div>
        </Link>

        <div className="flex-1 relative max-w-2xl">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-4 text-neutral-400 pointer-events-none" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search 100+ brands like Amazon, Myntra, Zomato…"
            className="w-full pl-11 pr-4 py-3 rounded-full bg-neutral-50 border border-neutral-200 text-sm placeholder:text-neutral-400 focus:outline-none focus:bg-white focus:border-[#C9A227] focus:ring-4 focus:ring-[#C9A227]/15 transition-all"
            data-testid="gift-search-input"
          />
        </div>

        <nav className="hidden lg:flex items-center gap-5 text-sm text-neutral-700 shrink-0">
          <button className="flex items-center gap-1 hover:text-[#8C6B0E] transition-colors">
            <span>Categories</span><ChevronDown className="size-3.5" />
          </button>
          <a href="#deals" className="hover:text-[#8C6B0E] transition-colors">Deals</a>
          <button className="hover:text-[#8C6B0E] transition-colors"><Heart className="size-4" /></button>
          <Link to="/gifts/orders" className="flex items-center gap-1.5 hover:text-[#8C6B0E] transition-colors" data-testid="gift-orders-nav">
            <User className="size-4" /><span>My orders</span>
          </Link>
          <Link
            to="/gifts/orders"
            className="inline-flex items-center gap-1.5 bg-gradient-to-r from-[#C9A227] to-[#8C6B0E] text-white text-sm font-medium px-5 py-2.5 rounded-full hover:shadow-lg hover:shadow-[#C9A227]/30 transition-all"
            data-testid="gift-cart-btn"
          >
            <ShoppingCart className="size-4" /><span>Cart</span>
          </Link>
        </nav>
      </div>
    </header>
  );
}

// ────────────────────────────────────────────────────────────────────────
function HeroCarousel() {
  const [idx, setIdx] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    timer.current = setInterval(() => setIdx((i) => (i + 1) % HERO_SLIDES.length), 5500);
    return () => clearInterval(timer.current);
  }, []);

  const go = (dir) => {
    clearInterval(timer.current);
    setIdx((i) => (i + dir + HERO_SLIDES.length) % HERO_SLIDES.length);
  };

  const s = HERO_SLIDES[idx];
  return (
    <section className="relative overflow-hidden" data-testid="gift-hero">
      <div className="absolute inset-0 transition-[background] duration-700" style={{ background: s.bg }} />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className={`lg:col-span-7 ${s.dark ? "text-white" : "text-neutral-900"}`}>
          <span
            className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-medium px-3 py-1 rounded-full border ${
              s.dark ? "bg-white/10 border-white/20 text-white" : "bg-white/80 border-[#C9A227]/40 text-[#8C6B0E]"
            }`}
          >
            <Sparkles className="size-3" /> {s.eyebrow}
          </span>
          <h1 className="mt-5 font-serif text-5xl lg:text-7xl font-semibold tracking-tight leading-[0.95]">
            {s.h1[0]}<br />
            <span style={{ color: s.accent }}>{s.h1[1]}</span>
          </h1>
          <p className={`mt-5 text-base lg:text-lg max-w-md leading-relaxed ${s.dark ? "text-neutral-300" : "text-neutral-600"}`}>{s.sub}</p>
          <div className="mt-8 flex items-center gap-4">
            <a
              href={s.cta.href}
              className={`inline-flex items-center gap-2 text-sm font-medium px-7 py-3.5 rounded-full transition-all shadow-lg hover:shadow-xl ${
                s.dark
                  ? "bg-gradient-to-r from-[#C9A227] to-[#8C6B0E] text-white hover:shadow-[#C9A227]/40"
                  : "bg-[#1A1A1A] text-white hover:bg-black"
              }`}
              data-testid="gift-hero-cta"
            >
              <ShoppingBag className="size-4" /> {s.cta.label}
            </a>
            <Link to="/gifts/orders" className={`text-sm font-medium underline-offset-4 hover:underline ${s.dark ? "text-neutral-300" : "text-neutral-700"}`}>
              Track an order →
            </Link>
          </div>
        </div>

        <div className="lg:col-span-5 relative h-[340px] hidden lg:block">
          <BrandCardStack accent={s.accent} dark={s.dark} />
        </div>
      </div>

      {/* Slide controls */}
      <button
        onClick={() => go(-1)}
        className="absolute left-3 top-1/2 -translate-y-1/2 size-10 rounded-full bg-white/80 backdrop-blur border border-neutral-200 hover:bg-white shadow-lg flex items-center justify-center transition-all"
        aria-label="Previous"
      ><ChevronLeft className="size-5" /></button>
      <button
        onClick={() => go(1)}
        className="absolute right-3 top-1/2 -translate-y-1/2 size-10 rounded-full bg-white/80 backdrop-blur border border-neutral-200 hover:bg-white shadow-lg flex items-center justify-center transition-all"
        aria-label="Next"
      ><ChevronRight className="size-5" /></button>
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-[#C9A227]" : "w-1.5 bg-neutral-400/50"}`}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
}

function BrandCardStack({ accent, dark }) {
  const cards = [
    { name: "Amazon", color: "#FF9900", offer: "Up to 5% off", denom: "₹500 – ₹10,000" },
    { name: "Myntra", color: "#FF3F6C", offer: "Flat 4% cashback", denom: "₹500 – ₹5,000" },
    { name: "Zomato", color: "#E23744", offer: "3% extra value", denom: "₹250 – ₹3,000" },
  ];
  const positions = [
    { top: 0, right: 0, rotate: 5, z: 30 },
    { top: 60, right: 80, rotate: -7, z: 20 },
    { top: 140, right: 30, rotate: 3, z: 10 },
  ];
  return (
    <>
      {cards.map((c, i) => {
        const p = positions[i];
        return (
          <div
            key={c.name}
            className="absolute w-[300px] h-[180px] rounded-2xl shadow-2xl hover:scale-[1.03] transition-transform duration-500 cursor-pointer"
            style={{
              top: p.top, right: p.right, transform: `rotate(${p.rotate}deg)`, zIndex: p.z,
              background: `linear-gradient(135deg, ${c.color} 0%, ${c.color}DD 100%)`,
            }}
          >
            <div className="absolute inset-[2px] rounded-2xl bg-white/95 backdrop-blur flex flex-col justify-between p-5">
              <div className="flex items-start justify-between">
                <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium">Gift card</div>
                <div className="text-[9px] uppercase tracking-wider text-white font-bold px-2 py-0.5 rounded-full"
                     style={{ background: c.color }}>{c.offer}</div>
              </div>
              <div>
                <div className="font-serif text-3xl font-semibold" style={{ color: c.color }}>{c.name}</div>
                <div className="text-[11px] text-neutral-500 mt-1">{c.denom}</div>
              </div>
              <div className="flex items-center justify-between text-[10px] text-neutral-400">
                <div className="tracking-widest">•••• QR-{(1234 + i * 17).toString()}</div>
                <Sparkles className="size-3.5" style={{ color: accent }} />
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────
function CategoryRow({ categories, activeCat, setActiveCat, totalBrands }) {
  // Always show a fixed set of category icons; merge counts from API
  const counts = Object.fromEntries(categories.map((c) => [c.category, c.count]));
  const items = Object.entries(CATEGORY_META).map(([key, meta]) => ({
    key, ...meta, count: counts[key] || 0,
  }));
  return (
    <section className="bg-white border-b border-neutral-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-7">
        <div className="flex items-baseline justify-between mb-5">
          <h3 className="font-serif text-2xl font-semibold tracking-tight">Shop by category</h3>
          <button
            onClick={() => setActiveCat("all")}
            className={`text-xs font-medium ${activeCat === "all" ? "text-[#8C6B0E]" : "text-neutral-500 hover:text-[#8C6B0E]"}`}
          >
            View all ({totalBrands}) →
          </button>
        </div>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-3 lg:gap-5">
          {items.map((it) => {
            const Icon = it.icon;
            const isActive = activeCat === it.key;
            return (
              <button
                key={it.key}
                onClick={() => setActiveCat(isActive ? "all" : it.key)}
                data-testid={`gift-cat-${it.key}`}
                className={`group flex flex-col items-center gap-2 p-3 rounded-xl border transition-all ${
                  isActive
                    ? "bg-[#FFFBF3] border-[#C9A227] shadow-sm"
                    : "bg-white border-neutral-200 hover:border-[#C9A227] hover:shadow-md hover:-translate-y-0.5"
                }`}
              >
                <div
                  className="size-14 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
                  style={{ background: `${it.color}15` }}
                >
                  <Icon className="size-6" style={{ color: it.color }} />
                </div>
                <div className="text-[11px] font-medium text-neutral-700 leading-tight text-center">{it.label}</div>
                {it.count > 0 && <div className="text-[10px] text-neutral-400">{it.count}</div>}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────
function OfferBanners() {
  const banners = [
    { tag: "Cashback", title: "Flat 8% back", sub: "On all premium brands", bg: "linear-gradient(135deg, #1A1A1A 0%, #2D2D2D 100%)", text: "#fff", accent: "#C9A227" },
    { tag: "New", title: "100+ brands", sub: "Just added this month", bg: "linear-gradient(135deg, #FFFBF3 0%, #FCE9B5 100%)", text: "#1A1A1A", accent: "#8C6B0E" },
    { tag: "Bulk", title: "Corporate gifting", sub: "1,000+ recipients, one invoice", bg: "linear-gradient(135deg, #FFF8E7 0%, #F0E1B0 100%)", text: "#1A1A1A", accent: "#C9A227" },
  ];
  return (
    <section id="deals" className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
      <div className="grid md:grid-cols-3 gap-5">
        {banners.map((b, i) => (
          <div
            key={i}
            className="relative rounded-2xl p-6 lg:p-7 overflow-hidden cursor-pointer hover:shadow-xl transition-all group"
            style={{ background: b.bg, color: b.text }}
          >
            <div className="text-[10px] uppercase tracking-[0.22em] font-medium opacity-70">{b.tag}</div>
            <h3 className="font-serif text-3xl font-semibold mt-2">{b.title}</h3>
            <p className="text-sm mt-1.5 opacity-80">{b.sub}</p>
            <div className="mt-5 inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: b.accent }}>
              Browse <ChevronRight className="size-3.5 group-hover:translate-x-1 transition-transform" />
            </div>
            <Sparkles className="absolute top-5 right-5 size-5 opacity-30" />
          </div>
        ))}
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────
function BrandSection({ id, title, subtitle, brands, loading, empty, onClear, testId, onTileClick }) {
  if (!loading && (!brands || brands.length === 0) && !empty) return null;
  return (
    <section id={id} className="max-w-7xl mx-auto px-6 lg:px-12 py-8 lg:py-10" data-testid={`gift-section-${testId}`}>
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h2 className="font-serif text-3xl lg:text-4xl font-semibold tracking-tight">{title}</h2>
          {subtitle && <div className="text-xs text-neutral-500 mt-1">{subtitle}</div>}
        </div>
        <a href="#brands" className="hidden md:inline-flex items-center gap-1 text-xs font-medium text-[#8C6B0E] hover:underline">
          View all <ChevronRight className="size-3.5" />
        </a>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="aspect-[5/4] rounded-xl bg-neutral-100 animate-pulse" />
          ))}
        </div>
      ) : empty ? (
        <div className="py-14 text-center">
          <div className="text-base text-neutral-500">No brands match your filters.</div>
          {onClear && (
            <button onClick={onClear} className="mt-3 text-sm font-medium text-[#8C6B0E] hover:underline">Clear filters</button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {brands.map((b) => <BrandTile key={`${testId}-${b.id}`} brand={b} onClick={onTileClick} />)}
        </div>
      )}
    </section>
  );
}

function BrandTile({ brand, onClick }) {
  const color = brand.brand_color && /^#?[0-9a-f]{3,8}$/i.test(brand.brand_color)
    ? (brand.brand_color.startsWith("#") ? brand.brand_color : `#${brand.brand_color}`)
    : "#C9A227";
  const minDen = brand.denominations?.[0];
  const startingAt = minDen?.is_variable ? minDen.min_value : minDen?.value;
  const cashbackPct = ((brand.id || "").charCodeAt(0) % 8) + 2; // mock 2-9% per brand for visual richness

  return (
    <Link
      to={`/gifts/${brand.slug}`}
      onClick={() => onClick && onClick(brand)}
      data-testid={`gift-brand-tile-${brand.slug}`}
      className="group relative rounded-2xl bg-white border border-neutral-200 overflow-hidden hover:shadow-[0_20px_40px_-20px_rgba(140,107,14,0.35)] hover:-translate-y-1 hover:border-[#C9A227]/50 transition-all"
    >
      {/* Logo block */}
      <div
        className="aspect-[5/4] flex items-center justify-center p-6 relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${color}08 0%, ${color}20 100%)` }}
      >
        {/* Decorative ring */}
        <div
          className="absolute inset-3 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{ boxShadow: `inset 0 0 0 1.5px ${color}40, inset 0 0 0 3.5px #C9A22725` }}
        />
        {brand.logo_url ? (
          <img src={brand.logo_url} alt={brand.name} className="max-h-16 max-w-[70%] object-contain transition-transform duration-300 group-hover:scale-105" loading="lazy" />
        ) : (
          <div className="font-serif text-2xl font-semibold" style={{ color }}>{brand.name}</div>
        )}
        {/* Cashback badge */}
        <span
          className="absolute top-3 left-3 text-[10px] uppercase tracking-wider text-white font-bold px-2 py-1 rounded-md shadow-md"
          style={{ background: `linear-gradient(135deg, ${color}, ${color}CC)` }}
        >
          Up to {cashbackPct}% off
        </span>
        {brand.is_featured && (
          <span className="absolute top-3 right-3 text-[9px] uppercase tracking-wider bg-gradient-to-r from-[#C9A227] to-[#8C6B0E] text-white px-2 py-0.5 rounded-full font-medium shadow">
            Featured
          </span>
        )}
      </div>

      {/* Brand meta */}
      <div className="px-4 py-3 border-t border-neutral-100">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="font-medium text-sm truncate">{brand.name}</div>
            <div className="text-[11px] text-neutral-500 mt-0.5">
              {startingAt ? `From ${formatINR(startingAt)}` : "Variable amount"}
            </div>
          </div>
          <div className="size-7 rounded-full bg-neutral-100 group-hover:bg-[#C9A227] group-hover:text-white flex items-center justify-center transition-colors">
            <ChevronRight className="size-4" />
          </div>
        </div>
        {brand.instant_delivery && (
          <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
            <Zap className="size-3" /> Instant delivery
          </div>
        )}
      </div>
    </Link>
  );
}

// ────────────────────────────────────────────────────────────────────────
function RecentlyViewedRail({ items, onClick, onClear }) {
  if (!items || items.length === 0) return null;
  return (
    <section className="bg-[#FFFBF3]/40 border-y border-[#E6D29B]/30" data-testid="gift-recently-viewed">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <div className="size-8 rounded-full bg-gradient-to-br from-[#FFFBF3] to-[#FCE9B5] flex items-center justify-center">
              <Clock className="size-4 text-[#8C6B0E]" />
            </div>
            <div>
              <h3 className="font-serif text-2xl font-semibold tracking-tight">Recently viewed</h3>
              <div className="text-[11px] text-neutral-500">Pick up where you left off</div>
            </div>
          </div>
          <button
            onClick={onClear}
            className="text-[11px] font-medium text-neutral-500 hover:text-[#8C6B0E] transition-colors"
            data-testid="gift-rv-clear"
          >
            Clear
          </button>
        </div>

        <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 scroll-smooth snap-x snap-mandatory" style={{ scrollbarWidth: "thin" }}>
          {items.map((b) => {
            const color = b.brand_color && /^#?[0-9a-f]{3,8}$/i.test(b.brand_color)
              ? (b.brand_color.startsWith("#") ? b.brand_color : `#${b.brand_color}`)
              : "#C9A227";
            const minDen = b.denominations?.[0];
            const startingAt = minDen?.is_variable ? minDen.min_value : minDen?.value;
            return (
              <Link
                key={`rv-${b.slug}`}
                to={`/gifts/${b.slug}`}
                onClick={() => onClick && onClick(b)}
                data-testid={`gift-rv-${b.slug}`}
                className="snap-start shrink-0 w-[180px] rounded-xl bg-white border border-neutral-200 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 hover:border-[#C9A227]/50 transition-all"
              >
                <div
                  className="aspect-[5/4] flex items-center justify-center p-4"
                  style={{ background: `linear-gradient(135deg, ${color}10 0%, ${color}25 100%)` }}
                >
                  {b.logo_url ? (
                    <img src={b.logo_url} alt={b.name} className="max-h-12 max-w-[80%] object-contain" loading="lazy" />
                  ) : (
                    <div className="font-serif text-lg font-semibold" style={{ color }}>{b.name}</div>
                  )}
                </div>
                <div className="px-3 py-2.5 border-t border-neutral-100">
                  <div className="font-medium text-xs truncate">{b.name}</div>
                  <div className="text-[10px] text-neutral-500 mt-0.5">
                    {startingAt ? `From ${formatINR(startingAt)}` : "Variable"}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────
function WhyUs() {
  const items = [
    { icon: Zap, t: "Instant delivery", d: "Voucher emailed & SMSed within seconds of payment." },
    { icon: ShieldCheck, t: "100% secure", d: "PCI-DSS payments via Razorpay. AES-256 encryption." },
    { icon: Sparkles, t: "Premium brands", d: "100+ curated brands across every category." },
    { icon: Gift, t: "Personalised", d: "Add a heartfelt note. Schedule for a future date." },
  ];
  return (
    <section className="bg-gradient-to-b from-white to-[#FFFBF3] border-t border-neutral-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14">
        <div className="text-center mb-10">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#8C6B0E] font-medium">Why QR Zone</div>
          <h2 className="font-serif text-3xl lg:text-4xl font-semibold tracking-tight mt-2">Built for thoughtful gifting</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <div key={it.t} className="p-6 rounded-2xl bg-white border border-neutral-200 hover:border-[#C9A227]/50 hover:shadow-lg transition-all">
                <div className="size-11 rounded-xl bg-gradient-to-br from-[#FFFBF3] to-[#FCE9B5] flex items-center justify-center mb-4">
                  <Icon className="size-5 text-[#8C6B0E]" />
                </div>
                <div className="font-medium text-sm">{it.t}</div>
                <div className="text-xs text-neutral-500 mt-1.5 leading-relaxed">{it.d}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0E0E0E] text-neutral-400 border-t border-neutral-900">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14 grid md:grid-cols-4 gap-10 text-sm">
        <div className="md:col-span-2 max-w-sm">
          <div className="flex items-center gap-2.5">
            <div className="size-9 rounded-lg bg-gradient-to-br from-[#C9A227] to-[#8C6B0E] flex items-center justify-center">
              <Gift className="size-5 text-white" />
            </div>
            <div className="font-serif text-2xl text-white">QR <span className="text-[#C9A227]">Zone</span></div>
          </div>
          <p className="mt-4 text-xs leading-relaxed text-neutral-500">
            Premium gift cards for every occasion. A PayFlex product, powered by Razorpay Engage.
            All voucher inventory reconciled to the rupee.
          </p>
          <div className="mt-5 flex items-center gap-3 text-[11px] text-neutral-500">
            <span className="inline-flex items-center gap-1"><ShieldCheck className="size-3.5 text-[#C9A227]" /> RBI-compliant</span>
            <span>·</span>
            <span>PCI-DSS</span>
          </div>
        </div>
        <div>
          <div className="text-white font-medium text-xs uppercase tracking-wider mb-4">Shop</div>
          <ul className="space-y-2.5 text-xs">
            <li><a href="#brands" className="hover:text-white">All brands</a></li>
            <li><a href="#deals" className="hover:text-white">Deals</a></li>
            <li><a href="#" className="hover:text-white">Bulk gifting</a></li>
            <li><Link to="/gifts/orders" className="hover:text-white">My orders</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-white font-medium text-xs uppercase tracking-wider mb-4">Company</div>
          <ul className="space-y-2.5 text-xs">
            <li><Link to="/login" className="hover:text-white">Partner sign-in</Link></li>
            <li><a href="#" className="hover:text-white">Terms</a></li>
            <li><a href="#" className="hover:text-white">Privacy</a></li>
            <li><a href="#" className="hover:text-white">Support</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-neutral-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 text-[11px] flex flex-col md:flex-row items-center justify-between gap-2">
          <div>© QR Zone · A PayFlex product</div>
          <div>Made with care in India · Powered by Razorpay</div>
        </div>
      </div>
    </footer>
  );
}
